'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { Trash2, TrendingUp, TrendingDown, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { cn } from '@/lib/utils';
import { useStepperContext } from '../ui/stepper';
import { useSessionFooter } from './NewSessionStepper';
import { PokerSiteLogo } from '../Dashboard/PokerSiteLogo';
import { getSpinGoBuyInValuesForSite } from '@/lib/spin-go-buyins';

export type SpinGoBuyInEntry = {
  buyInLevel: string;
  tournamentCount: string;
};

export type SpinGoSessionLine = {
  bankrollId: string;
  gameType: 'Normal' | 'Nitro';
  /** Niveaux de buy-in sélectionnés (sélection multiple) */
  entries: SpinGoBuyInEntry[];
  /** Un seul nombre de spins pour toute la ligne */
  tournamentCount: string;
  profit: string;
  rackback: string;
  cev: string;
  durationMinutes?: string;
};

interface SessionData {
  format?: string;
  universe?: 'Online' | 'Live';
  date?: Date;
  duration?: string;
  bankrolls?: Map<string, { bankrollId: string; newBalance: string }>;
}

const GAME_TYPE_OPTIONS: { value: 'Normal' | 'Nitro'; label: string }[] = [
  { value: 'Normal', label: 'Spin & Go' },
  { value: 'Nitro', label: 'Spin & Go Nitro' },
];

interface NewSessionStep2SessionsSpinGoProps {
  onComplete: (data: {
    bankrolls: Map<string, {
      bankrollId: string;
      newBalance: string;
      spinGoDetails: {
        gameType: string;
        durationMinutes?: string;
        buyInLevel?: string;
        tournamentCount: string;
        buyInTotal: string;
        rackback: string;
        profit?: string;
        cev?: string;
      }[];
    }>;
    duration?: string;
  }) => void;
  sessionData: SessionData;
}

export const NewSessionStep2SessionsSpinGo: React.FC<NewSessionStep2SessionsSpinGoProps> = ({
  onComplete,
  sessionData,
}) => {
  const t = useTranslations('sessions.new.stepper.step2Sessions');
  const { nextStep, previousStep } = useStepperContext();
  const setFooter = useSessionFooter()?.setFooter;
  const selectedBankrollIds = Array.from(sessionData.bankrolls?.keys() || []);
  const firstBankrollId = selectedBankrollIds[0] ?? '';

  const [lines, setLines] = useState<SpinGoSessionLine[]>(() => {
    const bankrollsMap = sessionData.bankrolls;
    if (!bankrollsMap) return [];
    const details: SpinGoSessionLine[] = [];
    bankrollsMap.forEach((entry, bankrollId) => {
      const spinGoDetails = (entry as { spinGoDetails?: { gameType: string; buyInLevel?: string; tournamentCount: string; profit?: string; rackback: string; cev?: string; durationMinutes?: string }[] }).spinGoDetails;
      if (spinGoDetails?.length) {
        // Grouper par (bankrollId, gameType) et regrouper les buy-ins en entries
        const byKey = new Map<string, SpinGoSessionLine>();
        spinGoDetails.forEach(d => {
          const key = `${bankrollId}|${d.gameType === 'Nitro' ? 'Nitro' : 'Normal'}`;
          const entry: SpinGoBuyInEntry = {
            buyInLevel: d.buyInLevel != null ? String(d.buyInLevel) : '',
            tournamentCount: d.tournamentCount != null ? String(d.tournamentCount) : '',
          };
          if (byKey.has(key)) {
            const existing = byKey.get(key)!;
            existing.entries.push(entry);
            const p = parseFloat(d.profit ?? '') || 0;
            const r = parseFloat(d.rackback ?? '') || 0;
            const c = parseFloat((d as { cev?: string }).cev ?? '') || 0;
            const prevP = parseFloat(existing.profit || '0') || 0;
            const prevR = parseFloat(existing.rackback || '0') || 0;
            const prevC = parseFloat(existing.cev || '0') || 0;
            existing.profit = String(prevP + p);
            existing.rackback = String(prevR + r);
            existing.cev = String(prevC + c);
          } else {
            byKey.set(key, {
              bankrollId,
              gameType: (d.gameType === 'Nitro' ? 'Nitro' : 'Normal') as 'Normal' | 'Nitro',
              entries: [entry],
              tournamentCount: '',
              profit: d.profit != null ? String(d.profit) : '',
              rackback: d.rackback ?? '',
              cev: (d as { cev?: string }).cev ?? '',
              durationMinutes: d.durationMinutes,
            });
          }
        });
        byKey.forEach((line) => {
          const total = line.entries.reduce((s, e) => s + (parseInt(e.tournamentCount, 10) || 0), 0);
          details.push({ ...line, tournamentCount: total > 0 ? String(total) : '' });
        });
      }
    });
    // Une ligne par défaut par bankroll si aucune donnée existante
    if (details.length) return details;
    return Array.from(bankrollsMap.keys()).map(bankrollId => ({
      bankrollId,
      gameType: 'Normal' as const,
      entries: [],
      tournamentCount: '',
      profit: '',
      rackback: '',
      cev: '',
    }));
  });
  const [bankrolls, setBankrolls] = useState<{ id: string; siteName: string; currency: string; currentBalance: string }[]>([]);

  useEffect(() => {
    const bankrollsMap = sessionData.bankrolls;
    if (!bankrollsMap?.size) return;
    const details: SpinGoSessionLine[] = [];
    bankrollsMap.forEach((entry, bankrollId) => {
      const spinGoDetails = (entry as { spinGoDetails?: { gameType: string; buyInLevel?: string; tournamentCount: string; profit?: string; rackback: string; cev?: string; durationMinutes?: string }[] }).spinGoDetails;
      if (spinGoDetails?.length) {
        const byKey = new Map<string, SpinGoSessionLine>();
        spinGoDetails.forEach(d => {
          const key = `${bankrollId}|${d.gameType === 'Nitro' ? 'Nitro' : 'Normal'}`;
          const buyInEntry: SpinGoBuyInEntry = {
            buyInLevel: d.buyInLevel != null ? String(d.buyInLevel) : '',
            tournamentCount: d.tournamentCount != null ? String(d.tournamentCount) : '',
          };
          if (byKey.has(key)) {
            const existing = byKey.get(key)!;
            existing.entries.push(buyInEntry);
            const p = parseFloat(d.profit ?? '') || 0;
            const r = parseFloat(d.rackback ?? '') || 0;
            const c = parseFloat((d as { cev?: string }).cev ?? '') || 0;
            const prevP = parseFloat(existing.profit || '0') || 0;
            const prevR = parseFloat(existing.rackback || '0') || 0;
            const prevC = parseFloat(existing.cev || '0') || 0;
            existing.profit = String(prevP + p);
            existing.rackback = String(prevR + r);
            existing.cev = String(prevC + c);
          } else {
            byKey.set(key, {
              bankrollId,
              gameType: (d.gameType === 'Nitro' ? 'Nitro' : 'Normal') as 'Normal' | 'Nitro',
              entries: [buyInEntry],
              tournamentCount: '',
              profit: d.profit != null ? String(d.profit) : '',
              rackback: d.rackback ?? '',
              cev: (d as { cev?: string }).cev ?? '',
              durationMinutes: d.durationMinutes,
            });
          }
        });
        byKey.forEach((line) => {
          const total = line.entries.reduce((s, e) => s + (parseInt(e.tournamentCount, 10) || 0), 0);
          details.push({ ...line, tournamentCount: total > 0 ? String(total) : '' });
        });
      }
    });
    if (details.length) setLines(details);
  }, [sessionData.bankrolls]);

  useEffect(() => {
    const fetchBankrolls = async () => {
      try {
        const res = await fetch('/api/bankrolls');
        const data = await res.json();
        if (data.bankrolls) {
          const ids = Array.from(sessionData.bankrolls?.keys() || []);
          const selected = data.bankrolls.filter((b: { id: string }) => ids.includes(b.id));
          setBankrolls(selected.map((b: { id: string; siteName: string; currency: string; currentBalance: string }) => ({
            id: b.id,
            siteName: b.siteName,
            currency: b.currency,
            currentBalance: b.currentBalance,
          })));
        }
      } catch (e) {
        console.error(e);
      }
    };
    fetchBankrolls();
  }, [sessionData.bankrolls]);

  const handleLineChange = (lineIndex: number, field: keyof Omit<SpinGoSessionLine, 'entries'>, value: string) => {
    const normalized =
      field === 'tournamentCount' ? (value === '' ? '' : value.replace(/[^0-9]/g, '')) : value;
    setLines(prev => prev.map((l, i) => (i === lineIndex ? { ...l, [field]: normalized } : l)));
  };

  /** Coche/décoche un niveau de buy-in dans la liste (sélection multiple) */
  const toggleBuyInLevel = (lineIndex: number, level: number) => {
    const levelStr = String(level);
    setLines(prev => prev.map((l, i) => {
      if (i !== lineIndex) return l;
      const idx = l.entries.findIndex(e => e.buyInLevel === levelStr);
      if (idx >= 0) {
        const entries = l.entries.filter((_, j) => j !== idx);
        return { ...l, entries: entries.length ? entries : [] };
      }
      return { ...l, entries: [...l.entries, { buyInLevel: levelStr, tournamentCount: '' }] };
    }));
  };

  const [openBuyInDropdownLineIndex, setOpenBuyInDropdownLineIndex] = useState<number | null>(null);
  const [buyInDropdownAnchor, setBuyInDropdownAnchor] = useState<{ bottom: number; left: number; width: number } | null>(null);
  const buyInDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (openBuyInDropdownLineIndex === null) return;
      const target = e.target as Element;
      if (target.closest('.buyin-dropdown-panel') || target.closest('[data-buyin-dropdown-trigger]')) return;
      setOpenBuyInDropdownLineIndex(null);
      setBuyInDropdownAnchor(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openBuyInDropdownLineIndex]);

  const addSession = (bankrollId: string) => {
    setLines(prev => [...prev, {
      bankrollId,
      gameType: 'Normal',
      entries: [],
      tournamentCount: '',
      profit: '',
      rackback: '',
      cev: '',
    }]);
  };

  const removeLine = (lineIndex: number) => {
    setLines(prev => prev.filter((_, i) => i !== lineIndex));
  };

  const handleContinue = useCallback(() => {
    const hasValidLine = lines.some(l =>
      l.entries.length > 0 && (parseInt(l.tournamentCount, 10) > 0 || l.profit.trim() !== '')
    );
    if (!hasValidLine) return;
    const updates = new Map<string, { bankrollId: string; newBalance: string; spinGoDetails: { gameType: string; durationMinutes?: string; buyInLevel?: string; tournamentCount: string; buyInTotal: string; rackback: string; profit?: string; cev?: string }[] }>();
    (sessionData.bankrolls || new Map()).forEach((_entry, bankrollId) => {
      const bankrollLines = lines.filter(l => l.bankrollId === bankrollId);
      const spinGoDetails: { gameType: string; durationMinutes?: string; buyInLevel?: string; tournamentCount: string; buyInTotal: string; rackback: string; profit?: string; cev?: string }[] = [];
      bankrollLines.forEach(l => {
        const validEntries = l.entries.filter(e => e.buyInLevel.trim() !== '');
        const totalSpinsLine = parseInt(l.tournamentCount, 10) || 0;
        const n = validEntries.length;
        const profitNum = parseFloat(l.profit) || 0;
        const rackbackNum = parseFloat(l.rackback) || 0;
        const cevNum = parseFloat(l.cev) || 0;
        if (n === 0) return;
        const perCount = Math.floor(totalSpinsLine / n);
        const remainder = totalSpinsLine - perCount * n;
        validEntries.forEach((e, idx) => {
          const tournamentCountNum = idx === n - 1 ? perCount + remainder : perCount;
          const buyInLevelNum = parseFloat(e.buyInLevel) || 0;
          const buyInTotal = buyInLevelNum * tournamentCountNum;
          const ratio = totalSpinsLine > 0 ? tournamentCountNum / totalSpinsLine : (idx === 0 ? 1 : 0);
          spinGoDetails.push({
            gameType: l.gameType,
            durationMinutes: l.durationMinutes || undefined,
            buyInLevel: e.buyInLevel || undefined,
            tournamentCount: String(tournamentCountNum),
            buyInTotal: String(buyInTotal),
            rackback: n > 1 ? String(rackbackNum * ratio) : l.rackback,
            profit: n > 1 ? String(profitNum * ratio) : (l.profit || undefined),
            cev: n > 1 ? (l.cev?.trim() ? String(cevNum * ratio) : undefined) : (l.cev?.trim() ? l.cev : undefined),
          });
        });
      });
      const bankroll = bankrolls.find(b => b.id === bankrollId);
      const totalProfit = bankrollLines.reduce((s, l) => s + (parseFloat(l.profit) || 0), 0);
      const currentBalance = bankroll ? parseFloat(bankroll.currentBalance) : 0;
      updates.set(bankrollId, {
        bankrollId,
        newBalance: String(currentBalance + totalProfit),
        spinGoDetails,
      });
    });
    onComplete({
      bankrolls: updates,
      duration: sessionData.duration,
    });
    nextStep();
  }, [lines, sessionData.bankrolls, sessionData.duration, bankrolls, onComplete, nextStep]);

  const isValid = lines.length > 0 && lines.some(l =>
    l.entries.length > 0 && (parseInt(l.tournamentCount, 10) > 0 || l.profit.trim() !== '')
  );

  useEffect(() => {
    if (!setFooter) return;
    setFooter({
      secondary: {
        label: t('previous'),
        onClick: previousStep,
      },
      primary: {
        label: t('continue'),
        disabled: !isValid,
        onClick: handleContinue,
      },
    });
    return () => setFooter(null);
  }, [setFooter, t, isValid, handleContinue, previousStep]);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t('title')}</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">{t('subtitleSpinGo')}</p>
      </div>

      {/* Une section par bankroll */}
      {selectedBankrollIds.map(bankrollId => {
        const bankroll = bankrolls.find(b => b.id === bankrollId);
        const bankrollLines = lines.filter(l => l.bankrollId === bankrollId);
        if (!bankroll) return null;
        return (
          <div key={bankrollId} className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <PokerSiteLogo siteName={bankroll.siteName} size={32} className="rounded-lg overflow-hidden" />
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">{bankroll.siteName}</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{bankrollLines.length} {t('sessionsCount')}</p>
                </div>
              </div>
              <Button
                type="button"
                onClick={() => addSession(bankrollId)}
                className="bg-gradient-to-r from-[#1a2332] via-[#0f172a] to-[#0c1220] hover:opacity-90 text-white font-semibold"
              >
                + {t('addSession')}
              </Button>
            </div>
            <div className="p-4 space-y-3">
              {bankrollLines.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 py-2">{t('noSessions')}</p>
              ) : (
                bankrollLines.map((line, localIndex) => {
                  const globalIndex = lines.findIndex(l => l === line);
                  const profitNum = parseFloat(line.profit) || 0;
                  return (
                    <div key={globalIndex} className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700 space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[auto_1fr_auto_1fr] gap-4 items-start">
                        <div className="min-w-0">
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 whitespace-nowrap">{t('type')} *</label>
                          <select
                            value={line.gameType}
                            onChange={(e) => handleLineChange(globalIndex, 'gameType', e.target.value as 'Normal' | 'Nitro')}
                            className="w-full h-10 px-3 py-2 rounded-lg border bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 min-w-0"
                          >
                            {GAME_TYPE_OPTIONS.map(opt => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                          </select>
                        </div>
                        <div className="min-w-0 relative buyin-dropdown-wrap">
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 whitespace-nowrap">{t('buyInLevel')} *</label>
                          <button
                            type="button"
                            data-buyin-dropdown-trigger
                            onClick={(e) => {
                              const rect = e.currentTarget.getBoundingClientRect();
                              setBuyInDropdownAnchor({ bottom: rect.bottom, left: rect.left, width: rect.width });
                              setOpenBuyInDropdownLineIndex(openBuyInDropdownLineIndex === globalIndex ? null : globalIndex);
                            }}
                            className={cn(
                              'w-full min-w-[10rem] h-10 px-3 py-2 text-left rounded-lg border bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 flex items-center justify-between gap-2',
                              line.entries.length > 0 && 'border-gold'
                            )}
                          >
                            <span className={line.entries.length > 0 ? 'text-gray-900 dark:text-white' : 'text-gray-500'}>
                              {line.entries.length > 0
                                ? line.entries.map(e => e.buyInLevel).filter(Boolean).join(', ')
                                : t('typePlaceholder')}
                            </span>
                            <ChevronDown className={cn('w-4 h-4 text-gray-400 shrink-0 transition-transform', openBuyInDropdownLineIndex === globalIndex && 'rotate-180')} />
                          </button>
                          {openBuyInDropdownLineIndex === globalIndex && buyInDropdownAnchor && (
                            <div
                              ref={buyInDropdownRef}
                              className="buyin-dropdown-panel fixed z-[101] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-2 max-h-[min(60vh,320px)] overflow-y-auto"
                              style={{
                                top: buyInDropdownAnchor.bottom + 4,
                                left: buyInDropdownAnchor.left,
                                width: Math.max(buyInDropdownAnchor.width, 220),
                              }}
                              onMouseDown={(e) => e.stopPropagation()}
                            >
                              {getSpinGoBuyInValuesForSite(bankroll.siteName).map((level) => {
                                const levelStr = String(level);
                                const isSelected = line.entries.some(e => e.buyInLevel === levelStr);
                                return (
                                  <button
                                    key={level}
                                    type="button"
                                    onClick={() => toggleBuyInLevel(globalIndex, level)}
                                    className={cn(
                                      'w-full flex items-center gap-2 px-3 py-2 text-left rounded-lg transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50',
                                      isSelected && 'bg-gold/10'
                                    )}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => {}}
                                      className="w-4 h-4 text-gold border-gray-300 rounded focus:ring-gray-900 dark:focus:ring-gray-100 shrink-0 pointer-events-none"
                                    />
                                    <span className="text-gray-900 dark:text-white">{level}</span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 whitespace-nowrap">{t('spinCount')} *</label>
                          <Input
                            type="number"
                            min="0"
                            value={line.tournamentCount}
                            onChange={(e) => handleLineChange(globalIndex, 'tournamentCount', e.target.value)}
                            placeholder="0"
                            className="w-full max-w-[6rem] h-10 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800"
                          />
                        </div>
                        <div className="flex flex-wrap gap-3 items-end lg:justify-end">
                          <div className="min-w-0">
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 whitespace-nowrap">{t('result')} ({bankroll.currency}) *</label>
                            <div className="flex rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 overflow-hidden focus-within:ring-2 focus-within:ring-gray-900 dark:focus-within:ring-gray-100">
                              <Input
                                type="number"
                                step="0.01"
                                value={line.profit}
                                onChange={(e) => handleLineChange(globalIndex, 'profit', e.target.value)}
                                placeholder="0"
                                className="border-0 focus-visible:ring-0 rounded-none bg-transparent min-w-0 w-24"
                              />
                              <span className="pr-2 flex items-center shrink-0">
                                {profitNum > 0 && <TrendingUp className="w-4 h-4 text-emerald-500" />}
                                {profitNum < 0 && <TrendingDown className="w-4 h-4 text-red-500" />}
                              </span>
                            </div>
                          </div>
                          <div className="min-w-0">
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 whitespace-nowrap">{t('rackback')}</label>
                            <Input
                              type="number"
                              step="0.01"
                              value={line.rackback}
                              onChange={(e) => handleLineChange(globalIndex, 'rackback', e.target.value)}
                              placeholder="0"
                              className="w-20 h-10 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800"
                            />
                          </div>
                          <div className="min-w-0">
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 whitespace-nowrap">{t('cev')}</label>
                            <Input
                              type="number"
                              step="0.01"
                              value={line.cev}
                              onChange={(e) => handleLineChange(globalIndex, 'cev', e.target.value)}
                              placeholder="0"
                              className="w-20 h-10 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800"
                            />
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeLine(globalIndex)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30 shrink-0 h-10 w-10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
